import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export interface AuthUser {
  id: string;
  email: string;
  name?: string;
  status: string;
  primary_org_id?: string;
  is_superuser?: boolean;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean; // True while checking auth status
  isInitialized: boolean; // True after first auth check completes
  sessionExpired: boolean; // True when logout was triggered by 401 (not manual)
}

interface UIState {
  sidebarOpen: boolean;
}

// Organization selection state - persisted to localStorage
interface OrgState {
  selectedOrgId: string | null;
}

interface AppState {
  auth: AuthState;
  ui: UIState;
  org: OrgState;

  // Auth actions
  setAuth: (user: AuthUser | null) => void;
  setAuthLoading: (isLoading: boolean) => void;
  setAuthInitialized: () => void;
  logout: () => void;
  expireSession: () => void;

  // UI actions
  setSidebarOpen: (open: boolean) => void;

  // Organization actions
  setSelectedOrgId: (orgId: string | null) => void;

  // Reset
  reset: () => void;
}

// -----------------------------------------------------------------------------
// Initial State
// -----------------------------------------------------------------------------

const initialAuthState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true, // Start loading until we check auth
  isInitialized: false,
  sessionExpired: false,
};

const initialUIState: UIState = {
  sidebarOpen: false,
};

const initialOrgState: OrgState = {
  selectedOrgId: null,
};

const initialState = {
  auth: initialAuthState,
  ui: initialUIState,
  org: initialOrgState,
};

// -----------------------------------------------------------------------------
// Store
// -----------------------------------------------------------------------------

// NOTE: We intentionally do NOT persist auth state to localStorage.
// Auth state is derived from HttpOnly cookies via /web/auth/me on page load.
// This is more secure as tokens never touch client-side JavaScript.
//
// However, we DO persist org selection to localStorage so it survives page reloads.

export const useAppStore = create<AppState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        setAuth: (user) =>
          set(
            () => ({
              auth: {
                user,
                isAuthenticated: !!user,
                isLoading: false,
                isInitialized: true,
                sessionExpired: false,
              },
            }),
            false,
            "setAuth"
          ),

        setAuthLoading: (isLoading) =>
          set(
            (state) => ({
              auth: { ...state.auth, isLoading },
            }),
            false,
            "setAuthLoading"
          ),

        setAuthInitialized: () =>
          set(
            (state) => ({
              auth: { ...state.auth, isInitialized: true, isLoading: false },
            }),
            false,
            "setAuthInitialized"
          ),

        logout: () =>
          set(
            () => ({
              auth: {
                ...initialAuthState,
                isLoading: false,
                isInitialized: true,
              },
            }),
            false,
            "logout"
          ),

        expireSession: () =>
          set(
            () => ({
              auth: {
                ...initialAuthState,
                isLoading: false,
                isInitialized: true,
                sessionExpired: true,
              },
            }),
            false,
            "expireSession"
          ),

        setSidebarOpen: (open) =>
          set(
            (state) => ({
              ui: { ...state.ui, sidebarOpen: open },
            }),
            false,
            "setSidebarOpen"
          ),

        setSelectedOrgId: (orgId) =>
          set(
            (state) => ({
              org: { ...state.org, selectedOrgId: orgId },
            }),
            false,
            "setSelectedOrgId"
          ),

        reset: () => set(initialState, false, "reset"),
      }),
      {
        name: "fhirfly-app-storage",
        // Only persist org selection, not auth or UI state
        partialize: (state) => ({ org: state.org }),
      }
    ),
    { name: "FHIRfly App Store" }
  )
);
